import { setActivePinia } from 'pinia'
import { useNuxtApp } from 'nuxt/app'

import { useAuthStore } from '@common/auth-layer/store/auth.store'
import { MigrationUtility } from '@common/auth-layer/utils/migration'
import { useGlobal } from '@common/core-layer/composables/useGlobal'
import { useLocationStore } from '@common/location-layer/store/location.store'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'

import { isCPAuthRoutePath } from '../utils/auth-route.utils'

import type { Pinia } from 'pinia'

const MIGRATION_GUARD_CHECKED_SESSION_KEY = 'clientportal:migrationGuard:checked'
let pendingMigrationGuardCheck: ReturnType<typeof MigrationUtility.redirectIfPendingMigration> | null = null

const hasCheckedMigrationStatusInSession = (): boolean => {
  try {
    return window.sessionStorage.getItem(MIGRATION_GUARD_CHECKED_SESSION_KEY) === 'true'
  } catch {
    return false
  }
}

const markMigrationStatusCheckedInSession = (): void => {
  try {
    window.sessionStorage.setItem(MIGRATION_GUARD_CHECKED_SESSION_KEY, 'true')
  } catch {
    /* ignore */
  }
}

const redirectIfPendingMigrationOnce = async (fullPath: string) => {
  if (hasCheckedMigrationStatusInSession()) return null

  pendingMigrationGuardCheck ||= (async () => {
    try {
      return await MigrationUtility.redirectIfPendingMigration(fullPath)
    } finally {
      markMigrationStatusCheckedInSession()
      pendingMigrationGuardCheck = null
    }
  })()

  return await pendingMigrationGuardCheck
}

/**
 * Auth v1 → v2 migration guard.
 *
 * Skip conditions (any one short-circuits the guard):
 *   - SSR: `process.server` — `MigrationUtility` is localStorage-backed.
 *   - GoKollab app: per spec, migration is WL/web only.
 *   - Auth v2 disabled for this location: nothing to migrate to.
 *   - User is not authenticated: the auth gate already handled the redirect; the
 *     pre-login priming flow lives in `LoginWithSecureCode` / `ForgotPassword`.
 *   - Already on a migration gate page: avoid redirect loops.
 *   - On any other auth-flow page (`/login`, `/signup`, …): let the auth flow
 *     finish before forcing the gate.
 */
export default defineNuxtRouteMiddleware(async to => {
  if (process.server) return

  const nuxtApp = useNuxtApp()
  const pinia = nuxtApp.$pinia as Pinia | undefined
  if (!pinia) return

  setActivePinia(pinia)

  const { isGokollab } = useGlobal()
  if (isGokollab) return

  const locationStore = useLocationStore(pinia)
  if (!locationStore.isAuthV2Enabled) return

  const authStore = useAuthStore(pinia)
  if (!authStore.isLoggedIn) return

  if (isCPAuthRoutePath(to.path)) return

  const target = await redirectIfPendingMigrationOnce(to.fullPath)
  if (!target) return

  const { CPNavigateTo } = useCPNavigation()
  return await CPNavigateTo({ ...target, options: { replace: true, ...(target.options || {}) } })
})
